//@ts-nocheck
import React, { useState } from "react";
import { Bell, Check } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useNotification from "@integration/application/hooks/useNotification.ts";
import NotificationCard from "@/components/cards/NotificationCard";
import { IParamsInivteNotification } from "@integration/application/interfaces/IParamsInivteNotification.ts";

const NotificationButton: React.FC = () => {
  const navigate = useNavigate();
  const [hasNotifications, setHasNotifications] = useState(true);
  const [open, setOpen] = useState(false);

  const {
    notifications,
    markAsRead,
    acceptionInvitationNotification,
    declineInvitationNotification,
  } = useNotification();

  const notificationList = notifications();

  const handleViewAll = () => {
    navigate("/notifications");
  };

  const handleAcceptInvitation = async (params: IParamsInivteNotification) => {
    await acceptionInvitationNotification(params);
    setOpen(false);
  };

  const handleDeclineInvitation = async (params: IParamsInivteNotification) => {
    await declineInvitationNotification(params);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <motion.div
          className="relative cursor-pointer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="bg-gradient-to-b from-gray-800/60 to-gray-900/60 p-2.5 rounded-full border border-gray-700/50 hover:border-red-500/30 shadow-md transition-all duration-300 flex items-center justify-center">
            <Bell
              size={24}
              className={`${hasNotifications ? "text-red-500 hover:text-red-400" : "text-white hover:text-gray-200"} transition-colors`}
            />
          </div>

          {hasNotifications && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-1 right-0 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded-full shadow-lg border border-gray-900/50"
            />
          )}
        </motion.div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-80 rounded-xl bg-gradient-to-b from-gray-800/80 to-gray-900/80 shadow-2xl border border-gray-700/50 p-4 z-50 backdrop-blur-sm relative overflow-hidden"
          sideOffset={5}
          align="end"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-2 rounded-lg shadow-md border border-red-500/10">
                <Bell size={18} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Notificações
              </h3>
            </div>
            {notificationList?.data?.length > 0 && (
              <button
                className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors duration-200 bg-gradient-to-b from-gray-800/60 to-gray-900/60 px-2 py-1 rounded-lg border border-gray-700/50 hover:border-red-500/30"
                onClick={() => {
                  // Mark all unread notifications as read
                  notificationList?.data
                    ?.filter((n) => !n.isRead)
                    .forEach((n) => {
                      // Assuming markAsRead function exists in useNotification hook
                      if (typeof markAsRead === "function") {
                        markAsRead(n.id);
                      }
                    });
                }}
              >
                <Check size={14} />
                <span>Marcar todas</span>
              </button>
            )}
          </div>
          <div className="h-0.5 w-24 bg-gradient-to-r from-red-500/30 to-transparent mb-4 relative z-10"></div>

          <div
            className="space-y-3 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar relative z-10"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#4B5563 #1F2937",
            }}
          >
            {notificationList?.data?.map((notification) => (
              <NotificationCard
                key={notification?.id}
                notification={notification}
                markAsRead={markAsRead}
                acceptionInvitationNotification={handleAcceptInvitation}
                declineInvitationNotification={handleDeclineInvitation}
                compact={true}
              />
            ))}
          </div>

          {notificationList?.data?.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center py-8 bg-gradient-to-b from-gray-800/30 to-gray-900/30 rounded-xl border border-gray-700/50 p-6 shadow-lg backdrop-blur-sm relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

              <div className="relative z-10">
                <div className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-gray-600/30 shadow-inner">
                  <Bell size={24} className="text-gray-400" />
                </div>

                <h3 className="text-lg font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">
                  Nenhuma notificação
                </h3>

                <div className="h-0.5 w-24 bg-gradient-to-r from-gray-500/30 to-transparent mx-auto mb-3"></div>

                <p className="text-gray-400 text-sm">
                  Nenhuma notificação no momento
                </p>
              </div>
            </motion.div>
          )}

          {notificationList?.data?.length > 0 && (
            <motion.button
              onClick={handleViewAll}
              className="w-full mt-4 py-2.5 rounded-xl bg-gradient-to-b from-gray-800/60 to-gray-900/60 text-sm text-gray-300 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 border border-gray-700/50 hover:border-red-500/30 shadow-md relative z-10"
              whileHover={{
                y: -2,
                boxShadow: "0 10px 25px -5px rgba(220, 38, 38, 0.1)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-1.5 rounded-lg shadow-sm border border-red-500/10">
                <Bell size={14} className="text-red-400" />
              </div>
              <span>Ver todas as notificações</span>
            </motion.button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default NotificationButton;
